import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnFeedQueryParameters, ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnAppliance, SnCapacity } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnAppliancesAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getApplianceList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnAppliance>> {
    return this._httpClient.get('/api/camera-appliances/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((camera) => new SnAppliance(camera)),
      })),
    );
  }

  public getAppliance(id: string): Observable<SnAppliance> {
    return this._httpClient
      .get(`/api/camera-appliances/${id}/`)
      .pipe(map((response: any) => new SnAppliance(response)));
  }

  public createAppliance(data: any): Observable<SnAppliance> {
    return this._httpClient
      .post('/api/camera-appliances/', data)
      .pipe(map((response: any) => new SnAppliance(response)));
  }

  public updateAppliance(id: string, data: any): Observable<SnAppliance> {
    return this._httpClient
      .patch(`/api/camera-appliances/${id}/`, data)
      .pipe(map((response: any) => new SnAppliance(response)));
  }

  public deleteAppliance(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('appliances.dialogs.deleteVideoLink'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/camera-appliances/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('appliances.messages.videoLinkDeleted')),
          error: () =>
            this._toastrService.error(t('appliances.errors.couldNotDeleteVideoLink')),
        }),
      );
  }

  public feedAppliances(params: ISnFeedQueryParameters): Observable<SnAppliance[]> {
    return this._httpClient.get('/api/camera-appliances/feed/', { params }).pipe(
      map((response: any) => {
        return response.map((locations) => new SnAppliance(locations));
      }),
    );
  }

  public replaceAppliance(applianceId: string, replaceId: string): Observable<void> {
    return this._httpClient
      .post<null>(`/api/camera-appliances/${applianceId}/replace/`, { targetId: replaceId })
      .pipe(
        tap({
          next: () => this._toastrService.success(t('appliances.messages.appliancesWasReplaced')),
          error: () =>
            this._toastrService.error(t('appliances.errors.couldNotReplaceAppliance')),
        }),
      );
  }

  public getCapacity(serialNumber: string): Observable<SnCapacity> {
    return this._httpClient.get(`/api/camera-appliances/${serialNumber}/capacity-metrics/`).pipe(
      map((response: any) => {
        const parseEpochSecondsToDate = (epochTime: any): Date | null => {
          if (typeof epochTime === 'string') {
            return new Date(parseInt(epochTime, 10) * 1000);
          } else if (typeof epochTime === 'number') {
            return new Date(epochTime * 1000);
          }
          return null;
        };

        response.capacityStatusUpdatedAt = parseEpochSecondsToDate(
          response.capacityStatusUpdatedAt,
        );
        response.networkSpeedUpdatedAt = parseEpochSecondsToDate(response.networkSpeedUpdatedAt);
        return new SnCapacity(response);
      }),
      tap({
        error: () =>
          this._toastrService.error(t('appliances.errors.couldNotRetrieveCoreCapacity')),
      }),
    );
  }

  public updateCameraStreamSetting(applianceId: string, data: any[]): Observable<any> {
    return this._httpClient.patch(
      `/api/camera-appliances/${applianceId}/cameras/bulk-update/`,
      data,
    );
  }

  public refreshNetworkSpeed(appliancesId: string): Observable<any> {
    return this._httpClient.post(
      `/api/camera-appliances/${appliancesId}/update_network_speed/`,
      null,
    );
  }
}
